import { SessionDto } from "@/common/dto/SessionDto";
import {QueryString} from "@/utils/QueryString";
import {TransAccount} from "@/common/dto/TransAccount";
import {TransDto} from "@/common/dto/TransDto";
import { TransFilter } from "@/common/req/TransFilter";
import {TransSum} from "@/common/dto/TransSum";
import {TransLimitDto} from "@/common/dto/TransLimitDto";
import {Memo} from "@/common/dto/Memo";
import {TransLimitSummary} from "@/common/dto/TransLimitSummary";
import {CreateTransLimitReq} from "@/common/req/CreateTransLimitReq";
import {UpdateTransLimitReq} from "@/common/req/UpdateTransLimitReq";
import {AccountData} from "@/common/req/AccountData";
import {MemoData} from "@/common/req/MemoData";
import {CloseAccountReq} from "@/common/req/CloseAccountReq";
import {MAX_TRANS_LIST_ITEM_COUNT} from "@/constatants";
import {DateTimeUtils} from "@/utils/DateTimeUtils";
import {TransData} from "@/common/dto/TransData";

const LS_KEY_SESSION_ID = "sessionId";

export class ApiClient {
    static instance = new ApiClient();

    private readonly apiEndPoint = "/api";
    private readonly observers = new Set<(session: SessionDto | undefined) => void>();
    private session?: SessionDto;

    private constructor() {
    }

    getSession(): SessionDto | undefined {
        return this.session;
    }

    addObserver(observer: (session: SessionDto | undefined) => void): void {
        this.observers.add(observer);
    }

    removeObserver(observer: (session: SessionDto | undefined) => void): void {
        this.observers.delete(observer);
    }

    async loadFromLocalStorage(): Promise<void> {
        const sessionId = localStorage.getItem(LS_KEY_SESSION_ID);
        if (sessionId) {
            try {
                this.session = await this.requestGet("/Session/" + sessionId);
                this.observers.forEach((o) => o(this.session));
            } catch (e) {
                // TODO: 만료된 세션인 경우 localStorage 에서 삭제
                console.error('Failed to load session:', e);
            }
        }
    }

    async login(username: string, password: string): Promise<SessionDto> {
        this.session = await this.requestPost("/Session", {username, password});
        localStorage.setItem(LS_KEY_SESSION_ID, this.session.id);
        this.observers.forEach((o) => o(this.session));
        return this.session;
    }

    async logout(): Promise<void> {
        if (!this.session) {
            console.warn('No session to logout');
            return;
        }

        try {
            await this.requestDelete("/Session");
        } catch (e) {
            console.warn('Failed to delete session. Deleting client session only', e);
        }

        localStorage.removeItem(LS_KEY_SESSION_ID);
        this.session = undefined;
        this.observers.forEach((o) => o(this.session));
    }

    async createAccount(data: AccountData): Promise<TransAccount> {
        return await this.requestPost("/Account", data);
    }

    async updateAccount(id: number, data: AccountData): Promise<TransAccount> {
        return await this.requestPut(`/Account/${id}`, data);
    }

    async closeAccount(id: number, data: CloseAccountReq): Promise<TransAccount> {
        return await this.requestPost(`/Account/${id}/close`, data);
    }

    async getAccount(id: number): Promise<TransAccount> {
        return this.requestGet(`/Account/${id}`);
    }

    async getAccounts(): Promise<TransAccount[]> {
        return this.requestGet("/Account");
    }

    async getBalances(fromDate?: string, toDate?: string): Promise<Record<number, TransSum>> {
        const q = new URLSearchParams();
        if (fromDate) q.append("transactDateFrom", fromDate);
        if (toDate) q.append("transactDateTo", toDate);
        const qs = q.toString();
        return this.requestGet("/AccountSummary" + (qs ? `?${qs}` : ''));
    }

    async getAccountMonthlySummary(accountId: number): Promise<Record<string, TransSum>> {
        return this.requestGet(`/Account/${accountId}/monthlySummary`);
    }

    async getAccountTransactionSum(accountId: number, fromDate?: string, toDate?: string): Promise<TransSum> {
        const q = new URLSearchParams();
        if (fromDate) q.append("transactDateFrom", fromDate);
        if (toDate) q.append("transactDateTo", toDate);
        const qs = q.toString();
        return this.requestGet(`/Account/${accountId}/summary` + (qs ? `?${qs}` : ''));
    }

    async createTransLimit(req: CreateTransLimitReq): Promise<TransLimitDto> {
        return this.requestPost("/TransLimit", req);
    }

    async updateTransLimit(transLimitId: number, req: UpdateTransLimitReq): Promise<TransLimitDto> {
        return this.requestPut(`/TransLimit/${transLimitId}`, req);
    }

    async deleteTransLimit(transLimitId: number): Promise<TransLimitDto> {
        return this.requestDelete(`/TransLimit/${transLimitId}`);
    }

    async getTransLimit(transLimitId: number): Promise<TransLimitDto> {
        return this.requestGet(`/TransLimit/${transLimitId}`);
    }

    async getTransLimits(accountId: number, applyDateFrom: string | undefined, applyDateTo: string | undefined): Promise<TransLimitDto[]> {
        const q = new URLSearchParams();
        q.append("accountId", accountId.toString());
        if (applyDateFrom) q.append("applyDateFrom", applyDateFrom);
        if (applyDateTo) q.append("applyDateTo", applyDateTo);
        const qs = q.toString();
        return this.requestGet(`/TransLimit` + (qs ? `?${qs}` : ''));
    }

    async getTransLimitSummary(accountId: number | undefined, applyDateFrom: string | undefined, applyDateTo: string | undefined): Promise<TransLimitSummary[]> {
        const q = new URLSearchParams();
        if (accountId) q.append("accountId", accountId.toString());
        if (applyDateFrom) q.append("applyDateFrom", applyDateFrom);
        if (applyDateTo) q.append("applyDateTo", applyDateTo);
        const qs = q.toString();
        return this.requestGet('/TransLimit/summary' + (qs ? `?${qs}` : ''));
    }

    async createTransaction(req: TransData): Promise<TransDto> {
        return await this.requestPost("/Transaction", req);
    }

    async updateTransaction(transId: number, req: TransData): Promise<TransDto> {
        return await this.requestPut(`/Transaction/${transId}`, req);
    }

    async deleteTransaction(transId: number): Promise<TransDto> {
        return await this.requestDelete(`/Transaction/${transId}`);
    }

    async getTransaction(transId: number): Promise<TransDto> {
        return await this.requestGet(`/Transaction/${transId}`);
    }

    async getTransactions(filter: TransFilter): Promise<TransDto[]> {
        const reqFilter = {...filter};

        reqFilter.maxItemCount = MAX_TRANS_LIST_ITEM_COUNT;
        if (reqFilter.createTimeFrom) {
            reqFilter.createTimeFrom = DateTimeUtils.parseLocalDate(reqFilter.createTimeFrom).toISOString();
        }
        if (reqFilter.createTimeTo) {
            const t = DateTimeUtils.parseLocalDate(reqFilter.createTimeTo);
            reqFilter.createTimeTo = new Date(t.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString();
        }

        const qs = QueryString.objectToQueryString(reqFilter, true);
        return await this.requestGet("/Transaction" + qs);
    }

    async getMemos(): Promise<Memo[]> {
        return await this.requestGet("/Memo");
    }

    async createMemo(data: MemoData): Promise<Memo> {
        return await this.requestPost("/Memo", data);
    }

    async updateMemo(id: number, data: MemoData): Promise<Memo> {
        return await this.requestPut(`/Memo/${id}`, data);
    }

    private async requestGet<T>(path: string): Promise<T> {
        return (await this.request('GET', path)).json();
    }

    private async requestPost<T>(path: string, body: any): Promise<T> {
        return (await this.request('POST', path, body)).json();
    }

    private async requestPut<T>(path: string, body: any): Promise<T> {
        return (await this.request('PUT', path, body)).json();
    }

    private async requestPatch<T>(path: string, body: any): Promise<T> {
        return (await this.request('PATCH', path, body)).json();
    }

    private async requestDelete<T>(path: string): Promise<T> {
        return (await this.request('DELETE', path)).json();
    }

    private async request(method: string, path: string, body?: any): Promise<Response> {
        const headers: HeadersInit = {
            "Content-Type": "application/json",
        };
        if (this.session) {
            headers.Authorization = "Bearer " + this.session.id;
        }

        const res = await fetch(`${this.apiEndPoint}${path}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
        });

        await this.handleError(res);
        return res;
    }

    private async handleError(response: Response) {
        if (!response.ok) {
            if (response.status === 401) {
                console.error('API authentication failed. Removing session: ' + response.body);
                localStorage.removeItem(LS_KEY_SESSION_ID);
                this.session = undefined;
                this.observers.forEach((o) => o(this.session));
            }

            let errorMessage = "Unexpected error";
            try {
                const body = await response.json();
                if (body && body.message) {
                    errorMessage = body.message;
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }
    }
}
